/**
 * §02.7 Numeral disc — docs/design-system/plate-and-hairline/02-Primitives.dc.html
 *
 * Every numeral inside a container (wizard steps, anatomy callouts, ranked
 * positions, step spines) goes through this one component. It exists because
 * flex-centring a digit centres its EM BOX, not the glyph — Archivo's metrics
 * (unitsPerEm 1000, ascender 1005, descender 250, capHeight 730) put the
 * digit's visual centre 12.5/1000 em below the box centre at line-height 1,
 * so a "centred" numeral always sits low. The correction:
 *   ((ascender − capHeight) − descender) / 2 = −0.0125em, plus an extra
 *   −0.010em optical correction for the bottom-heavy weight of 0/3/6/8/9.
 * Total −0.0225em, rounded to the nearest 0.5px so it lands on a whole
 * device pixel at 2x DPR — see the three-tier table below.
 *
 * Geometry contract (§17.1 "Read"): the disc is radius-round at 1 digit,
 * a radius-pill stadium at 2 digits (never a wider circle — that visibly
 * grows the badge and breaks a step spine's column). At 3+ digits, or below
 * the 18px floor, the ring is dropped and it becomes a bare tabular numeral,
 * because the ring costs more legibility than it adds at that point.
 */
export interface NumeralDiscProps {
  /** The value shown. `done` renders a checkmark instead. */
  n: number;
  /** Diameter in px. Spec sizes: 20 callout, 32 step, 44 wizard, 64 plate step. */
  d?: number;
  /** Step has been completed — shows a check and, with `filled`, an accent fill. */
  done?: boolean;
  /** This is the current/active step — ink ring instead of the muted edge ring. */
  active?: boolean;
  /** Solid fill (accent when done, ink otherwise) instead of an outline ring. */
  filled?: boolean;
  /** Future / not-yet-reachable step — dims to 0.45 opacity. */
  dim?: boolean;
  /** Render the 3px focus ring (only meaningful when the disc labels a focusable step). */
  focus?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/** −0.0225em rounded to the nearest 0.5px step, per diameter band. */
function opticalOffsetPx(d: number): number {
  if (d <= 28) return -0.5;
  if (d <= 44) return -1;
  return -1.5;
}

export function NumeralDisc({
  n,
  d = 32,
  done = false,
  active = false,
  filled = false,
  dim = false,
  focus = false,
  className,
  style,
}: NumeralDiscProps) {
  const s = String(n);
  const digits = s.length;
  const three = digits >= 3;
  const bare = three || d < 18;
  const fontSize = Math.round(d * 0.44 * 10) / 10;
  const offset = opticalOffsetPx(d);
  const on = done || active;
  const numeralColor = filled ? "#fff" : on ? "var(--ls-color-ink)" : "var(--ls-color-ink-numeral)";

  if (bare) {
    return (
      <span
        data-ls
        className={className}
        aria-hidden="true"
        style={{
          fontSize,
          fontWeight: 800,
          color: numeralColor,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em",
          lineHeight: 1,
          display: "inline-block",
          fontFamily: "var(--tls-font-display)",
          ...style,
        }}
      >
        {s}
      </span>
    );
  }

  const two = digits === 2;
  // 1 digit: perfect circle, width = diameter. 2 digits: stadium, width grows
  // by 0.58x the numeral size rather than the ring widening into a bigger
  // circle — that keeps a step spine's discs on one visual column.
  const width = two ? Math.round(d + fontSize * 0.58) : d;
  const ringColor = on ? "var(--ls-color-ink)" : "var(--ls-color-edge)";

  return (
    <span
      data-ls
      aria-hidden="true"
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width,
        height: d,
        flex: "none",
        boxSizing: "border-box",
        borderRadius: "var(--ls-radius-pill)",
        border: "none",
        // `border: 1.5px solid` computes to 1px at both DPR 1 and DPR 2
        // (Chromium snaps border-width to the device pixel grid) — see
        // checkbox.tsx for the same fix. box-shadow's spread isn't snapped,
        // so an inset shadow renders the true 1.5px ring §02.7 specifies.
        boxShadow: filled ? "none" : `inset 0 0 0 1.5px ${ringColor}`,
        background: filled ? (done ? "var(--ls-color-accent)" : "var(--ls-color-ink)") : "transparent",
        outline: focus ? "3px solid var(--ls-color-accent-lift)" : "none",
        outlineOffset: 3,
        opacity: dim ? 0.45 : 1,
        ...style,
      }}
    >
      <span
        style={{
          fontSize,
          fontWeight: 800,
          lineHeight: 1,
          color: numeralColor,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: two ? "-0.02em" : "0",
          transform: `translateY(${offset}px)`,
          display: "block",
          fontFamily: "var(--tls-font-display)",
        }}
      >
        {done ? "✓" : s}
      </span>
    </span>
  );
}
